import { Button } from 'flowbite-react';
import { Chat, MessageInput, MessageList } from '@pubnub/react-chat-components';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { usePubNub } from 'pubnub-react';
import BackArrowButtonIcon from 'assets/svg/BackArrowButton';
import ChatIcon from 'assets/svg/Chat';
import { useDataValues } from 'providers/DataValues';
import { useChatModal } from 'providers/ChatModalProvider';
import { useModals } from 'providers/Modals';
import { ChatMembership } from 'providers/ChatModalProvider/reducer/types';
import PhishingAlert from './alert.phishing';
import FrozenChannelAlert from './alert.frozen-channel';
import useMessageRenderer from '../hooks/useMessageRenderer';
import useChatMembersQuery from '../hooks/members.query';
import useIsFocused from '../../../hooks/useIsFocused';
import { useChatSendNotifyMutation } from '../hooks/notify.mutation';

type Props = {
  activeChannel?: ChatMembership;
  setActiveChannel(v?: ChatMembership): void;
  anotherUsername?: string;
  loadingAnotherUser?: boolean;
  channels?: string[];
};

export default function SectionMessagesList({
  activeChannel,
  setActiveChannel,
  anotherUsername,
  loadingAnotherUser,
  channels,
}: Props) {
  const { userId, appValues, profile } = useDataValues();
  const { parseToken, getToken } = usePubNub();

  const navigate = useNavigate();
  const { closeChat } = useChatModal();
  const goToDetails = () => {
    if (userId === activeChannel?.custom?.trade_creator_id) {
      activeChannel?.custom?.trade_id && navigate(`/trades/${activeChannel.custom.trade_id}`);
    } else {
      activeChannel?.custom?.order_id && navigate(`/orders/${activeChannel.custom.order_id}`);
    }
    closeChat();
  };

  const productLabel = [
    appValues?.catalogIDMapping.get((activeChannel?.custom?.product_id || '').toString())?.label,
    appValues?.catalogIDMapping.get((activeChannel?.custom?.variety_id || '').toString())?.label,
  ]
    .filter(Boolean)
    .join('/');

  const { open: openModal } = useModals();

  const { data, isLoading } = useChatMembersQuery(activeChannel?.id || '');

  const messageRenderer = useMessageRenderer({
    ownUsername: profile.username || '',
    anotherUsername,
    lastRead: data?.find((c) => c.uuid.id !== profile?.chat_id)?.custom?.lastReadTimetoken as any,
  });

  const { onChannelSelected } = useChatModal();

  const isFocused = useIsFocused();

  useEffect(() => {
    if (activeChannel && isFocused) {
      onChannelSelected(activeChannel.id);
      const interval = setInterval(() => {
        onChannelSelected(activeChannel.id);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [activeChannel, activeChannel?.id, isFocused]);

  const { mutateAsync: sendNotify } = useChatSendNotifyMutation();

  const onMessageSent = useCallback(() => {
    sendNotify({
      tradeId: activeChannel?.custom?.trade_id?.toString() || '',
      orderId: activeChannel?.custom?.order_id?.toString() || '',
      isTrader: userId == activeChannel?.custom?.trade_creator_id,
    }).catch(console.error);
  }, [activeChannel, userId, sendNotify]);

  // check whether the current PubNub token grants write access to the active channel,
  // looking at both explicit resource grants and pattern-based grants
  const isAllowedToType = useMemo(() => {
    const token = getToken();
    if (!token) return false;

    const channelId = activeChannel?.id || '';
    const parsedToken = parseToken(token);

    // explicit resource grant for this channel
    if (parsedToken.resources?.channels?.[channelId]?.write) return true;

    // pattern-based grant (e.g. ".*" or "trade-.*")
    const patterns = parsedToken.patterns?.channels;
    if (patterns) {
      for (const pattern of Object.keys(patterns)) {
        if (patterns[pattern]?.write && new RegExp(`^${pattern}$`).test(channelId)) return true;
      }
    }

    return false;
  }, [activeChannel?.id, getToken, parseToken]);

  return (
    <section className={'flex flex-col h-full overflow-auto border-l border-l-neutral-100'}>
      <header className={'p-4 border-b border-b-neutral-200 gap-4 flex items-center min-h-20'}>
        <button type="button" className={'lg:hidden'} onClick={() => setActiveChannel(undefined)}>
          <BackArrowButtonIcon size={24} className={'text-white'} />
        </button>

        <ChatIcon size={32} />

        <div className={'space-y-1'}>
          <button
            type={'button'}
            className={'text-brand-black break-words text-sm font-semibold md:text-base hover:underline'}
            onClick={goToDetails}
          >
            {activeChannel?.name}
          </button>

          {activeChannel?.custom && (
            <p className={'text-brand-gray-dark text-xs break-words md:text-sm'}>
              {!!productLabel && (
                <>
                  {'Product:  '}
                  <b>{productLabel}</b>
                  {'  '}
                </>
              )}

              {!!anotherUsername && (
                <>
                  {'User:  '}
                  <b>{anotherUsername}</b>
                </>
              )}
            </p>
          )}
        </div>

        {isAllowedToType && (
          <Button
            size="xs"
            color="dark"
            className="ml-auto"
            onClick={() => {
              openModal('report-problem', { tradeId: activeChannel?.custom?.trade_id as string });
            }}
          >
            Report
          </Button>
        )}
      </header>

      {activeChannel && !loadingAnotherUser && !isLoading ? (
        <Chat
          currentChannel={activeChannel?.id || ''}
          channels={channels}
          users={
            (data || []).map((m) => {
              return {
                ...m.uuid,
                custom: { ...((m.uuid as any)?.custom || {}), lastReadTimetoken: m.custom?.lastReadTimetoken },
              };
            }) as any[]
          }
        >
          <MessageList fetchMessages={200} messageRenderer={messageRenderer} />

          <footer className={'pt-2 border-t border-t-neutral-200 space-y-2'}>
            <PhishingAlert />

            {!isAllowedToType ? (
              <FrozenChannelAlert />
            ) : (
              <MessageInput placeholder={'Type message...'} onSend={onMessageSent} />
            )}
          </footer>
        </Chat>
      ) : (
        <div className={'mt-auto'} />
      )}
    </section>
  );
}
